/**
 * Card Classification Utilities
 * 
 * Auto-detect R1-R6 ramp primitives and C1-C5 draw categories
 * from Scryfall data (oracle text, keywords, type line)
 */

import type { Card } from '../App';

// ============================================
// RAMP CLASSIFICATION (R1-R6)
// ============================================

export interface RampDetectionResult {
    rampCategory: 'R1' | 'R2' | 'R3' | 'R4' | 'R5' | 'R6' | 'none';
    hasManaAbility: boolean;
    manaProduced: number;
    manaColors: string[];
    requiresTap: boolean;
    gatedBySummoningSickness: boolean;
    hasHaste: boolean;
    isSacrifice: boolean;
    extraLandPlays?: number;
    searchType?: 'hand' | 'battlefield_tapped' | 'battlefield_untapped';
    deckThinning?: number;
    manaMultiplier?: number;
    appliesToCreaturesOnly?: boolean;
    causesUntappedEntry?: boolean;
    untapsLandsEndStep?: boolean;
}

/**
 * Detect ramp category and properties from oracle text and type line
 */
export function detectRampProperties(
    oracleText: string,
    typeLine: string,
    keywords: string[] = []
): RampDetectionResult {
    const text = (oracleText || '').toLowerCase();
    const type = (typeLine || '').toLowerCase();
    const kwds = keywords.map(k => k.toLowerCase());

    const result: RampDetectionResult = {
        rampCategory: 'none',
        hasManaAbility: false,
        manaProduced: 0,
        manaColors: [],
        requiresTap: false,
        gatedBySummoningSickness: false,
        hasHaste: kwds.includes('haste') || text.includes('haste'),
        isSacrifice: false,
    };

    // R1: Extra land plays
    if (text.includes('additional land') ||
        text.includes('play an additional land') ||
        text.includes('play two additional lands')) {
        result.rampCategory = 'R1';

        if (text.includes('two additional lands')) {
            result.extraLandPlays = 2;
        } else if (text.includes('three additional lands')) {
            result.extraLandPlays = 3;
        } else {
            result.extraLandPlays = 1;
        }

        // Creatures with R1 are gated by summoning sickness for their static
        if (type.includes('creature')) {
            result.gatedBySummoningSickness = true;
        }

        return result;
    }

    // R2: Put land from hand onto battlefield
    if ((text.includes('put') && text.includes('land') && text.includes('hand') && text.includes('battlefield')) ||
        (text.includes('put a land card from your hand onto the battlefield'))) {
        result.rampCategory = 'R2';
        result.isSacrifice = true; // One-time effect
        return result;
    }

    // R3: Search for land
    if (text.includes('search your library for') && text.includes('land')) {
        result.rampCategory = 'R3';
        result.isSacrifice = true; // Spell resolves once

        // Determine search type
        if (text.includes('onto the battlefield tapped')) {
            result.searchType = 'battlefield_tapped';
        } else if (text.includes('onto the battlefield')) {
            result.searchType = 'battlefield_untapped';
        } else if (text.includes('put it into your hand')) {
            result.searchType = 'hand';
        } else {
            result.searchType = 'battlefield_tapped'; // Default assumption
        }

        // Estimate deck thinning
        if (text.includes('two') || text.includes('up to two')) {
            result.deckThinning = 2;
        } else if (text.includes('three')) {
            result.deckThinning = 3;
        } else {
            result.deckThinning = 1;
        }

        return result;
    }

    // R4: Permanent mana source (tap for mana)
    if (text.includes('{t}: add') ||
        text.includes('{tap}: add') ||
        text.includes(', {t}: add') ||
        // Lands implicitly have mana abilities
        (type.includes('land') && !text.includes("doesn't untap"))) {
        result.rampCategory = 'R4';
        result.hasManaAbility = true;
        result.requiresTap = true;

        // Detect mana produced
        if (text.includes('add {c}{c}') || text.includes('add two mana')) {
            result.manaProduced = 2;
            result.manaColors = ['C'];
        } else if (text.includes('add {c}{c}{c}') || text.includes('add three mana')) {
            result.manaProduced = 3;
            result.manaColors = ['C'];
        } else if (text.includes('add one mana of any color') || text.includes('any type')) {
            result.manaProduced = 1;
            result.manaColors = ['any'];
        } else {
            result.manaProduced = 1;

            // Detect specific colors
            const colors: string[] = [];
            if (text.includes('add {w}')) colors.push('W');
            if (text.includes('add {u}')) colors.push('U');
            if (text.includes('add {b}')) colors.push('B');
            if (text.includes('add {r}')) colors.push('R');
            if (text.includes('add {g}')) colors.push('G');
            if (text.includes('add {c}')) colors.push('C');

            result.manaColors = colors.length > 0 ? colors : ['C'];
        }

        // Creatures are gated by summoning sickness
        if (type.includes('creature')) {
            result.gatedBySummoningSickness = !result.hasHaste;
        }

        return result;
    }

    // R5: Mana multipliers
    if ((text.includes('whenever') && text.includes('tap') && text.includes('mana') && text.includes('add')) ||
        text.includes('for each mana') ||
        text.includes('triple that mana') ||
        text.includes('instead produce')) {
        result.rampCategory = 'R5';

        if (text.includes('triple')) {
            result.manaMultiplier = 3;
        } else {
            result.manaMultiplier = 2;
        }

        if (text.includes('creature')) {
            result.appliesToCreaturesOnly = true;
        }

        if (type.includes('creature')) {
            result.gatedBySummoningSickness = !result.hasHaste;
        }

        return result;
    }

    // R6: Timing modifiers
    if (text.includes('enters the battlefield untapped') ||
        text.includes("enters tapped. it untaps during") ||
        text.includes("don't cause") && text.includes('tap') ||
        text.includes("untap all lands you control")) {
        result.rampCategory = 'R6';

        if (text.includes('untapped')) {
            result.causesUntappedEntry = true;
        }
        if (text.includes('untap all lands')) {
            result.untapsLandsEndStep = true;
        }

        return result;
    }

    // Basic lands
    if (type.includes('basic land')) {
        result.rampCategory = 'R4';
        result.hasManaAbility = true;
        result.manaProduced = 1;
        result.requiresTap = true;

        if (type.includes('plains')) result.manaColors = ['W'];
        else if (type.includes('island')) result.manaColors = ['U'];
        else if (type.includes('swamp')) result.manaColors = ['B'];
        else if (type.includes('mountain')) result.manaColors = ['R'];
        else if (type.includes('forest')) result.manaColors = ['G'];
        else result.manaColors = ['C'];

        return result;
    }

    return result;
}

// ============================================
// DRAW CLASSIFICATION (C1-C5)
// ============================================

export interface DrawDetectionResult {
    drawCategory: 'C1' | 'C2' | 'C3' | 'C4' | 'C5' | 'none';
    cardsDrawn: number;
    isRepeatableDraw: boolean;
    isConditionalDraw: boolean;
    drawTrigger?: string;
    scryAmount?: number;
    surveilAmount?: number;
    millAmount?: number;
}

/**
 * Detect draw category and properties from oracle text
 */
export function detectDrawProperties(
    oracleText: string,
    typeLine: string
): DrawDetectionResult {
    const text = (oracleText || '').toLowerCase();
    const type = (typeLine || '').toLowerCase();

    const result: DrawDetectionResult = {
        drawCategory: 'none',
        cardsDrawn: 0,
        isRepeatableDraw: false,
        isConditionalDraw: false,
    };

    // Detect scry/surveil amounts
    const scryMatch = text.match(/scry (\d+)/);
    if (scryMatch) {
        result.scryAmount = parseInt(scryMatch[1], 10);
    }

    const surveilMatch = text.match(/surveil (\d+)/);
    if (surveilMatch) {
        result.surveilAmount = parseInt(surveilMatch[1], 10);
    }

    const millMatch = text.match(/mill (\d+)/);
    if (millMatch) {
        result.millAmount = parseInt(millMatch[1], 10);
    }

    // C5: Mass draw (wheel effects)
    if (text.includes('discard your hand') && text.includes('draw')) {
        result.drawCategory = 'C5';
        result.cardsDrawn = 7; // Typical wheel
        const drawMatch = text.match(/draw (\d+|seven|that many)/);
        if (drawMatch) {
            if (drawMatch[1] === 'seven') result.cardsDrawn = 7;
            else if (drawMatch[1] !== 'that many') result.cardsDrawn = parseInt(drawMatch[1], 10);
        }
        return result;
    }

    // C3: Repeatable draw engines (enchantments/permanents that draw each turn)
    if (type.includes('enchantment') || type.includes('creature') || type.includes('artifact')) {
        if ((text.includes('at the beginning of your upkeep') ||
            text.includes('at the beginning of each upkeep') ||
            text.includes('at the beginning of your end step')) &&
            text.includes('draw')) {
            result.drawCategory = 'C3';
            result.cardsDrawn = 1;
            result.isRepeatableDraw = true;

            const drawMatch = text.match(/draw (\d+|a|one|two|three)/);
            if (drawMatch) {
                if (drawMatch[1] === 'a' || drawMatch[1] === 'one') result.cardsDrawn = 1;
                else if (drawMatch[1] === 'two') result.cardsDrawn = 2;
                else if (drawMatch[1] === 'three') result.cardsDrawn = 3;
                else result.cardsDrawn = parseInt(drawMatch[1], 10) || 1;
            }

            return result;
        }
    }

    // C4: Conditional/triggered draw
    if (text.includes('whenever') && text.includes('draw')) {
        result.drawCategory = 'C4';
        result.cardsDrawn = 1;
        result.isRepeatableDraw = true;
        result.isConditionalDraw = true;

        // Try to extract trigger
        const triggerMatch = text.match(/whenever ([^,]+),/);
        if (triggerMatch) {
            result.drawTrigger = triggerMatch[1];
        }

        return result;
    }

    // C1: Cantrips (draw 1, often with selection)
    if ((type.includes('instant') || type.includes('sorcery')) &&
        text.includes('draw a card') &&
        !text.includes('draw two') &&
        !text.includes('draw three')) {
        result.drawCategory = 'C1';
        result.cardsDrawn = 1;
        return result;
    }

    // C2: Raw draw spells (draw 2+)
    if ((type.includes('instant') || type.includes('sorcery')) &&
        (text.includes('draw two') || text.includes('draw three') ||
            text.includes('draw four') || text.includes('draw 2') ||
            text.includes('draw 3') || text.includes('draw 4'))) {
        result.drawCategory = 'C2';

        if (text.includes('draw two') || text.includes('draw 2')) result.cardsDrawn = 2;
        else if (text.includes('draw three') || text.includes('draw 3')) result.cardsDrawn = 3;
        else if (text.includes('draw four') || text.includes('draw 4')) result.cardsDrawn = 4;
        else result.cardsDrawn = 2;

        return result;
    }

    return result;
}

// ============================================
// COMBINED CLASSIFICATION
// ============================================

/**
 * Classify a card's ramp and draw properties from Scryfall data
 */
export function classifyCard(
    oracleText: string,
    typeLine: string,
    keywords: string[] = []
): {
    ramp: RampDetectionResult;
    draw: DrawDetectionResult;
} {
    return {
        ramp: detectRampProperties(oracleText, typeLine, keywords),
        draw: detectDrawProperties(oracleText, typeLine),
    };
}

/**
 * Apply detected properties to a Card object
 */
export function applyClassificationToCard(
    card: Partial<Card>,
    oracleText: string,
    typeLine: string,
    keywords: string[] = []
): Partial<Card> {
    const classification = classifyCard(oracleText, typeLine, keywords);
    const { ramp, draw } = classification;

    const updates: Partial<Card> = {
        ...card,
        oracleText,
    };

    // Apply ramp properties
    if (ramp.rampCategory !== 'none') {
        updates.rampCategory = ramp.rampCategory;
        updates.hasManaAbility = ramp.hasManaAbility;
        updates.manaProduced = ramp.manaProduced;
        updates.manaColors = ramp.manaColors;
        updates.requiresTap = ramp.requiresTap;
        updates.gatedBySummoningSickness = ramp.gatedBySummoningSickness;
        updates.hasHaste = ramp.hasHaste;
        updates.isSacrifice = ramp.isSacrifice;

        if (ramp.extraLandPlays !== undefined) {
            updates.extraLandPlays = ramp.extraLandPlays;
        }
        if (ramp.searchType !== undefined) {
            updates.searchType = ramp.searchType;
            updates.deckThinning = ramp.deckThinning;
        }
        if (ramp.manaMultiplier !== undefined) {
            updates.manaMultiplier = ramp.manaMultiplier;
            updates.appliesToCreaturesOnly = ramp.appliesToCreaturesOnly;
        }
        if (ramp.causesUntappedEntry !== undefined) {
            updates.causesUntappedEntry = ramp.causesUntappedEntry;
        }
        if (ramp.untapsLandsEndStep !== undefined) {
            updates.untapsLandsEndStep = ramp.untapsLandsEndStep;
        }
    }

    // Apply draw properties
    if (draw.drawCategory !== 'none') {
        updates.drawCategory = draw.drawCategory;
        updates.cardsDrawn = draw.cardsDrawn;
        updates.isRepeatableDraw = draw.isRepeatableDraw;
        updates.isConditionalDraw = draw.isConditionalDraw;
        updates.drawTrigger = draw.drawTrigger;
    }

    // Apply bonus modifiers
    if (draw.scryAmount) {
        updates.scryAmount = draw.scryAmount;
    }
    if (draw.surveilAmount) {
        updates.surveilAmount = draw.surveilAmount;
    }
    if (draw.millAmount) {
        updates.millAmount = draw.millAmount;
    }

    return updates;
}

// ============================================
// KNOWN CARD OVERRIDES
// ============================================

/**
 * Manual overrides for cards that need special classification
 */
export const CARD_CLASSIFICATION_OVERRIDES: Record<string, Partial<RampDetectionResult & DrawDetectionResult>> = {
    // R1: Extra land plays
    'Azusa, Lost but Seeking': { rampCategory: 'R1', extraLandPlays: 2 },
    'Exploration': { rampCategory: 'R1', extraLandPlays: 1 },
    'Oracle of Mul Daya': { rampCategory: 'R1', extraLandPlays: 1, gatedBySummoningSickness: true },
    'Dryad of the Ilysian Grove': { rampCategory: 'R1', extraLandPlays: 1, gatedBySummoningSickness: true },
    'Burgeoning': { rampCategory: 'R2', isConditionalDraw: false }, // Put land from hand on opponent's turns

    // R3: Land tutors
    'Cultivate': { rampCategory: 'R3', searchType: 'battlefield_tapped', deckThinning: 2 },
    "Kodama's Reach": { rampCategory: 'R3', searchType: 'battlefield_tapped', deckThinning: 2 },
    'Rampant Growth': { rampCategory: 'R3', searchType: 'battlefield_tapped', deckThinning: 1 },
    "Nature's Lore": { rampCategory: 'R3', searchType: 'battlefield_untapped', deckThinning: 1 },
    'Three Visits': { rampCategory: 'R3', searchType: 'battlefield_untapped', deckThinning: 1 },
    'Skyshroud Claim': { rampCategory: 'R3', searchType: 'battlefield_untapped', deckThinning: 2 },
    'Farseek': { rampCategory: 'R3', searchType: 'battlefield_tapped', deckThinning: 1 },

    // R4: Mana rocks/dorks
    'Sol Ring': { rampCategory: 'R4', hasManaAbility: true, manaProduced: 2, manaColors: ['C'], gatedBySummoningSickness: false },
    'Mana Crypt': { rampCategory: 'R4', hasManaAbility: true, manaProduced: 2, manaColors: ['C'], gatedBySummoningSickness: false },
    'Arcane Signet': { rampCategory: 'R4', hasManaAbility: true, manaProduced: 1, manaColors: ['any'], gatedBySummoningSickness: false },
    'Llanowar Elves': { rampCategory: 'R4', hasManaAbility: true, manaProduced: 1, manaColors: ['G'], gatedBySummoningSickness: true },
    'Elvish Mystic': { rampCategory: 'R4', hasManaAbility: true, manaProduced: 1, manaColors: ['G'], gatedBySummoningSickness: true },
    'Birds of Paradise': { rampCategory: 'R4', hasManaAbility: true, manaProduced: 1, manaColors: ['any'], gatedBySummoningSickness: true },

    // R5: Multipliers
    'Nyxbloom Ancient': { rampCategory: 'R5', manaMultiplier: 3, gatedBySummoningSickness: true },
    'Leyline of Abundance': { rampCategory: 'R5', manaMultiplier: 1, appliesToCreaturesOnly: true },

    // R6: Timing
    'Amulet of Vigor': { rampCategory: 'R6', causesUntappedEntry: true },
    'Wilderness Reclamation': { rampCategory: 'R6', untapsLandsEndStep: true },

    // C3: Draw engines
    'Phyrexian Arena': { drawCategory: 'C3', cardsDrawn: 1, isRepeatableDraw: true },
    'Rhystic Study': { drawCategory: 'C4', cardsDrawn: 1, isRepeatableDraw: true, isConditionalDraw: true },
    'Sylvan Library': { drawCategory: 'C3', cardsDrawn: 2, isRepeatableDraw: true },

    // C5: Wheels
    'Wheel of Fortune': { drawCategory: 'C5', cardsDrawn: 7 },
    'Windfall': { drawCategory: 'C5', cardsDrawn: 7 },
};
